import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { formatDistanceToNow } from 'date-fns';
import {
    Phone, Mail, MessageCircle, Instagram, Smartphone,
    Filter, Search, RefreshCw, AlertCircle, Plus, X,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import { getleadsList, getleadstatus, convertLeadToMember } from '../../services/apiServices';
import LeftPane from '../../components/LeftPane';
import Header from '../../components/Header';
import '../styles/leadList.css';
import { useLocation, useNavigate } from "react-router";

const LeadsList = () => {
    const gymId = JSON.parse(localStorage.getItem("loggedUserInfo")).gymid;
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({ total: 0, unread: 0, notResponded: 0, quoteSent: 0, leads: 0, trials: 0, converted: 0 });
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
    const [filters, setFilters] = useState({ status: 'all', priority: 'all', search: '' });
    const [showFilters, setShowFilters] = useState(false);
    const { socket } = useSocket();
    const navigate = useNavigate();
    const fetchLeads = useCallback(async (opts = {}) => {
        const silent = opts.silent === true;
        const pageNum =
            opts.page !== undefined && opts.page !== null ? opts.page : pagination.page;
        try {
            if (!silent) setLoading(true);
            const response = await getleadsList(
                filters.status,
                filters.search,
                filters.priority,
                gymId,
                pageNum,
                pagination.limit
            );
            if (!response || response === 'false') return;
            const data = await response.json();
            if (data) {
                setLeads(data.data || []);
                setStats((prev) => data.stats || prev);
                setPagination((prev) => data.pagination || prev);
            }
        } catch (err) {
            console.error('Error fetching leads:', err);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [gymId, filters.status, filters.search, filters.priority, pagination.page, pagination.limit]);

    const fetchLeadsRef = useRef(fetchLeads);
    fetchLeadsRef.current = fetchLeads;

    const fetchStats = useCallback(async () => {
        try {
            const response = await getleadstatus(gymId);
            if (!response || response === 'false') return;
            const data = await response.json();
            if (data?.data) setStats(data.data);
        } catch (err) {
            console.error(err);
        }
    }, [gymId]);

    useEffect(() => {
        if (gymId) fetchLeads();
    }, [gymId, filters.status, filters.priority, pagination.page, fetchLeads]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (gymId) {
                setPagination((p) => ({ ...p, page: 1 }));
                fetchLeadsRef.current({ page: 1 });
            }
        }, 500);
        return () => clearTimeout(t);
    }, [filters.search, gymId]);

    /**
     * Server broadcasts to gym room using event NAMES (e.g. lead_updated), not an event named gym_${id}.
     * Clients receive these after join_gym (see SocketContext).
     */
    useEffect(() => {
        if (!socket || !gymId) return;

        const norm = (id) => (id != null ? String(id) : '');

        const onLeadUpdated = (data) => {
            const leadId = norm(data?.leadId);
            if (!leadId) return;
            const updates = data?.updates || {};
            let needRefetch = false;
            setLeads((prev) => {
                const idx = prev.findIndex((l) => norm(l._id) === leadId);
                if (idx === -1) {
                    needRefetch = true;
                    return prev;
                }
                const next = [...prev];
                next[idx] = { ...next[idx], ...updates };
                return next;
            });
            if (needRefetch) fetchLeads({ silent: true });
            fetchStats();
        };

        const onMemberMessageReceived = (data) => {
            if (data && data.success === false) return;
            const partial = data?.lead;
            const lastMsg = data?.conversation?.lastMessage;
            const lmText =
                lastMsg && typeof lastMsg === 'object' && lastMsg.text != null
                    ? lastMsg.text
                    : typeof lastMsg === 'string'
                        ? lastMsg
                        : undefined;
            const lmAt =
                lastMsg && typeof lastMsg === 'object' && lastMsg.timestamp
                    ? lastMsg.timestamp
                    : data?.conversation?.updatedAt;

            if (!partial?._id) {
                fetchLeads({ silent: true });
                fetchStats();
                return;
            }
            const leadId = norm(partial._id);
            let needRefetch = false;
            setLeads((prev) => {
                const idx = prev.findIndex((l) => norm(l._id) === leadId);
                if (idx === -1) {
                    needRefetch = true;
                    return prev;
                }
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    ...partial,
                    ...(lmText != null ? { lastMessage: lmText } : {}),
                    ...(lmAt != null ? { lastMessageAt: lmAt } : {})
                };
                if (partial.unreadCount != null) next[idx].unreadCount = partial.unreadCount;
                return next;
            });
            if (needRefetch) fetchLeads({ silent: true });
            fetchStats();
        };

        const onConversationUpdated = (data) => {
            const convId = norm(data?.conversationId);
            if (!convId) return;
            const lm = data?.lastMessage;
            const text =
                lm && typeof lm === 'object' && lm.text != null ? lm.text : undefined;
            const ts =
                lm && typeof lm === 'object' && lm.timestamp ? lm.timestamp : data?.updatedAt;
            let needRefetch = false;
            setLeads((prev) => {
                const idx = prev.findIndex((l) => norm(l.conversationId) === convId);
                if (idx === -1) {
                    needRefetch = true;
                    return prev;
                }
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    ...(text != null ? { lastMessage: text } : {}),
                    ...(ts != null ? { lastMessageAt: ts } : {}),
                    ...(data?.unreadCount != null ? { unreadCount: data.unreadCount } : {})
                };
                return next;
            });
            if (needRefetch) fetchLeads({ silent: true });
            fetchStats();
        };

        socket.on('lead_updated', onLeadUpdated);
        socket.on('member_message_received', onMemberMessageReceived);
        socket.on('conversation_updated', onConversationUpdated);

        return () => {
            socket.off('lead_updated', onLeadUpdated);
            socket.off('member_message_received', onMemberMessageReceived);
            socket.off('conversation_updated', onConversationUpdated);
        };
    }, [socket, gymId, fetchLeads, fetchStats]);

    const getChannelIcon = (channel) => {
        switch (channel?.toLowerCase()) {
            case 'phone': return <Phone size={14} />;
            case 'whatsapp': return <MessageCircle size={14} className="lead-channel-whatsapp" />;
            case 'email': return <Mail size={14} />;
            case 'instagram': return <Instagram size={14} className="lead-channel-instagram" />;
            case 'sms': return <Smartphone size={14} />;
            default: return <MessageCircle size={14} className="lead-channel-whatsapp" />;
        }
    };

    const getPriorityClass = (p) => ({ Hot: 'lead-badge-hot', Warm: 'lead-badge-warm', Cold: 'lead-badge-cold' }[p] || '');
    const getStatusClass = (s) => ({
        Lead: 'lead-badge-lead',
        Trial: 'lead-badge-trial',
        Converted: 'lead-badge-converted',
        Member: 'lead-badge-member',
        Lost: 'lead-badge-lost',
        'Not Responded': 'lead-badge-not-responded',
        'Quote Sent': 'lead-badge-quote-sent',
    }[s] || '');

    const handleLeadClick = (lead) => {
        navigate(`/whatsapp/leads/${lead._id}`)
    };

    const handleConvertLead = async (e, lead) => {
        e.stopPropagation();
        if (lead.memberId) return;
        if (!window.confirm(
            `Convert "${lead.name}" to a member? This creates a member record in your gym.`
        )) return;
        try {
            const res = await convertLeadToMember(lead._id);
            const json = await res?.json?.().catch(() => ({}));
            if (!res?.ok) {
                alert(json.message || 'Conversion failed');
                return;
            }
            fetchLeads();
            fetchStats();
        } catch (err) {
            console.error(err);
            alert(err.message || 'Conversion failed');
        }
    };

    const clearFilters = () => setFilters({ status: 'all', priority: 'all', search: '' });
    const hasActiveFilters =
        filters.status !== 'all' ||
        filters.priority !== 'all' ||
        filters.search !== '';

    /** Same `status` values as the filter dropdown (plus `all` for Total). */
    const handleStatCardClick = (statusValue) => {
        setPagination((p) => ({ ...p, page: 1 }));
        setFilters((f) => {
            if (statusValue === 'all') {
                return { ...f, status: 'all' };
            }
            if (f.status === statusValue) {
                return { ...f, status: 'all' };
            }
            return { ...f, status: statusValue };
        });
    };

    const statCards = [
        { label: 'Total Leads', value: stats.total, cls: 'lead-stat-total', status: 'all' },
        { label: 'Unread', value: stats.unread, cls: 'lead-stat-unread', status: 'Unread' },
        { label: 'Not Responded', value: stats.notResponded, cls: 'lead-stat-nr', status: 'Not Responded' },
        { label: 'Quote Sent', value: stats.quoteSent, cls: 'lead-stat-quote', status: 'Quote Sent' },
        { label: 'Leads', value: stats.leads, cls: 'lead-stat-leads', status: 'Lead' },
        { label: 'Trials', value: stats.trials, cls: 'lead-stat-trials', status: 'Trial' },
        { label: 'Converted', value: stats.converted, cls: 'lead-stat-converted', status: 'Member' },
    ];

    const getQuoteStatus = (quotes) => {
        if (!quotes || quotes.length === 0) {
            return { status: 'Not Sent', type: 'not-sent' };
        }

        // Sort quotes by createdAt in descending order to get the latest
        const sortedQuotes = [...quotes].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
        );

        const latestQuote = sortedQuotes[0];

        return {
            status: latestQuote.status === 'sent' ? 'Sent' :
                latestQuote.status === 'draft' ? 'Draft' :
                    latestQuote.status,
            type: latestQuote.status === 'sent' ? 'sent' :
                latestQuote.status === 'draft' ? 'draft' : 'other'
        };
    };
    return (
        <div className="lead-root">
            <LeftPane isactive="16" />
            <Header />
            <div className="lead-main gym-main rightContent">

                {/* ── Top bar ── */}
                <div className="lead-topbar">
                    <div className="lead-topbar-left">
                        <h1 className="lead-title">Leads &amp; Inbox</h1>
                        <span className="lead-beta-badge" aria-label="Beta feature">Beta</span>
                        <button className="lead-refresh-btn" onClick={fetchLeads} title="Refresh">
                            <RefreshCw size={16} />
                        </button>
                    </div>
                    {/* <button className="lead-add-btn">
                        <Plus size={16} /> Add Lead
                    </button> */}
                </div>

                {/* ── Stat cards ── */}
                <div className="lead-stats-row">
                    {statCards.map(({ label, value, cls, status: statusKey }) => (
                        <button
                            key={label}
                            type="button"
                            className={`lead-stat-card ${cls}${filters.status === statusKey ? ' lead-stat-card-active' : ''}`}
                            onClick={() => handleStatCardClick(statusKey)}
                        >
                            <div className="lead-stat-label">{label}</div>
                            <div className="lead-stat-value">{value}</div>
                        </button>
                    ))}
                </div>

                {/* ── Search + filter bar ── */}
                <div className="lead-search-bar">
                    <div className="lead-search-wrap">
                        <Search size={16} className="lead-search-icon" />
                        <input
                            className="lead-search-input"
                            type="text"
                            placeholder="Search by name, phone, email..."
                            value={filters.search}
                            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
                        />
                    </div>
                    <button
                        className={`lead-filter-btn ${showFilters ? 'lead-filter-btn-active' : ''}`}
                        onClick={() => setShowFilters(p => !p)}
                    >
                        <Filter size={15} /> Filters
                        {hasActiveFilters && <span className="lead-filter-dot" />}
                    </button>
                    {/* {hasActiveFilters && ( */}
                    <button className="lead-clear-btn" onClick={clearFilters}>
                        <X size={14} /> Clear
                    </button>
                    {/* )} */}
                </div>

                {/* ── Filter panel ── */}
                {showFilters && (
                    <div className="lead-filter-panel">
                        <div className="lead-filter-group">
                            <label className="lead-filter-label">Status</label>
                            <select className="lead-filter-select" value={filters.status}
                                onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
                                <option value="all">All Status</option>
                                <option value="Unread">Unread</option>
                                <option value="Lead">Lead</option>
                                <option value="Trial">Trial</option>
                                <option value="Member">Converted</option>
                                {/* <option value="Lost">Lost</option> */}
                                <option value="Not Responded">Not Responded</option>
                                <option value="Quote Sent">Quote Sent</option>
                            </select>
                        </div>

                        <div className="lead-filter-group">
                            <label className="lead-filter-label">Priority</label>
                            <select className="lead-filter-select" value={filters.priority}
                                onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))}>
                                <option value="all">All Priority</option>
                                <option value="Hot">Hot</option>
                                <option value="Warm">Warm</option>
                                <option value="Cold">Cold</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* ── Table ── */}
                <div className="lead-table-wrap">
                    {loading ? (
                        <div className="lead-loading">
                            <div className="lead-spinner" />
                        </div>
                    ) : (
                        <table className="lead-table">
                            <thead className="lead-thead">
                                <tr>
                                    <th className="lead-th">Lead Name</th>
                                    <th className="lead-th">Contact &amp; Channel</th>
                                    <th className="lead-th">Status</th>
                                    <th className="lead-th">Priority</th>
                                    <th className="lead-th">Quote</th>
                                    <th className="lead-th">Staff Assigned</th>
                                    <th className="lead-th">Last Contact</th>
                                    <th className="lead-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leads.map(lead => (
                                    <tr key={lead._id} className="lead-tr" onClick={() => handleLeadClick(lead)}>

                                        {/* Name + source */}
                                        <td className="lead-td" data-label="Lead">
                                            <div className="lead-name-cell">
                                                {lead.unreadCount > 0 && <span className="lead-unread-dot" />}
                                                <div>
                                                    <div className="lead-name-text">
                                                        {lead.name}
                                                        {lead.unreadCount > 0 && (
                                                            <span className="lead-unread-badge">{lead.unreadCount}</span>
                                                        )}
                                                    </div>
                                                    {lead.source && (
                                                        <div className="lead-source-text">Source: {lead.source}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Contact + channel */}
                                        <td className="lead-td" data-label="Contact">
                                            <div className="lead-contact-cell">
                                                {lead.phone && (
                                                    <div className="lead-contact-row">
                                                        <Phone size={13} className="lead-contact-icon" />
                                                        <span>{lead.phone}</span>
                                                    </div>
                                                )}
                                                {lead.email && (
                                                    <div className="lead-contact-row">
                                                        <Mail size={13} className="lead-contact-icon" />
                                                        <span>{lead.email}</span>
                                                    </div>
                                                )}
                                                {lead.channel && (
                                                    <div className={`lead-channel-pill lead-channel-${lead.channel?.toLowerCase()}`}>
                                                        {getChannelIcon(lead.channel)}
                                                        <span>{lead.channel}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="lead-td" data-label="Status">
                                            {lead.status === 'Not Responded' ? (
                                                <span className="lead-not-responded-badge">
                                                    <span className="lead-nr-dot" /> Not Responded
                                                </span>
                                            ) : (
                                                <span className={`lead-badge ${getStatusClass(lead.status)}`}>
                                                    {lead.status}
                                                </span>
                                            )}
                                        </td>

                                        {/* Priority */}
                                        <td className="lead-td" data-label="Priority">
                                            {lead.priority && (
                                                <span className={`lead-badge lead-badge-pill ${getPriorityClass(lead.priority)}`}>
                                                    {lead.priority}
                                                </span>
                                            )}
                                        </td>

                                        {/* Quote */}
                                        <td className="lead-td" data-label="Quote">
                                            {(() => {
                                                const quoteInfo = getQuoteStatus(lead.quotes);
                                                if (quoteInfo.status === 'Not Sent') {
                                                    return (
                                                        <span className="lead-quote-not-sent">
                                                            Not Sent
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <span className={`lead-quote-${quoteInfo.type}`}>
                                                        {quoteInfo.status === 'Sent' && (
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <line x1="22" y1="2" x2="11" y2="13" />
                                                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                                            </svg>
                                                        )}
                                                        {quoteInfo.status === 'Draft' && (
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                                                                <path d="M4 20h16" />
                                                            </svg>
                                                        )}
                                                        {quoteInfo.status}
                                                    </span>
                                                );
                                            })()}
                                        </td>

                                        {/* Assigned */}
                                        <td className="lead-td" data-label="Assigned">
                                            <span className={`lead-assigned ${!lead.assignedTo ? 'lead-unassigned' : ''}`}>
                                                {lead.assignedTo || 'Unassigned'}
                                            </span>
                                        </td>

                                        {/* Last contact */}
                                        <td className="lead-td" data-label="Last contact">
                                            <span className="lead-last-contact">
                                                {lead.lastMessageAt
                                                    ? new Date(lead.lastMessageAt).toLocaleDateString('en-GB').replace(/\//g, '-')
                                                    : '—'}
                                            </span>
                                        </td>

                                        {/* Actions */}
                                        <td className="lead-td lead-td-actions" data-label="" onClick={e => e.stopPropagation()}>
                                            <div className="lead-actions">
                                                <button className="lead-action-view"
                                                    onClick={() => handleLeadClick(lead)}>View</button>
                                                {!lead.memberId && lead.status !== 'Member' && (
                                                    <button
                                                        type="button"
                                                        className="lead-action-convert"
                                                        onClick={(e) => handleConvertLead(e, lead)}
                                                    >
                                                        Convert
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {leads.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="lead-empty">
                                            <AlertCircle size={28} className="lead-empty-icon" />
                                            <p>No leads found</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ── Pagination ── */}
                {pagination.pages > 1 && (
                    <div className="lead-pagination">
                        <div className="lead-pagination-left">
                            <span>Rows per page:</span>
                            <select className="lead-rows-select"
                                value={pagination.limit}
                                onChange={e => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}>
                                {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="lead-pagination-right">
                            <span className="lead-pagination-info">
                                {((pagination.page - 1) * pagination.limit) + 1}–
                                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                            </span>
                            <button className="lead-page-btn"
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                                <ChevronLeft size={16} />
                            </button>
                            <button className="lead-page-btn"
                                disabled={pagination.page === pagination.pages}
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadsList;